<template>
    <tr v-if="(discount as Discount).amount > 0">
        <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4 tw-pl-10">
            <span class="tw-mr-5 tw-inline-block">
                {{ (discount as Discount).description }}
            </span>
            <TwButton
                cssClass="tw-bg-transparent hover:tw-text-orange-medium tw-transition-all tw-duration-200 tw-inline-block"
                @emit-click="clickedRemoveDiscount(discount as Discount)"
                :isLoading="processingRemove"
                :isDisabled="false"
                buttonText="remove"
            />
        </td>
        <td
            class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4"
            :aria-busy="showTotalsShimmer"
        >
            <span :class="amountValueClass">
                -${{ formattedDiscountAmount }}
            </span>
        </td>
    </tr>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

// Data
import { useBasketStore } from "../../stores/basket";
import type Discount from '../../types/Discount';

const props = defineProps<{ discount: Discount }>();

import TwButton from '../../components/TwButton.vue';

const emit = defineEmits(['emitRemoveDiscount', 'emitRemoveDiscount'])

const processingRemove = ref(false);

const basketStore = useBasketStore();

const showTotalsShimmer = computed(() => basketStore.hasPendingMutations);

const amountValueBaseClass = 'amount-value tw-inline-flex tw-justify-end tw-min-w-[5.5rem] tw-leading-none tw-px-1';
const amountValueShimmerClass = `${amountValueBaseClass} amount-value--loading`;

const amountValueClass = computed(() => (showTotalsShimmer.value ? amountValueShimmerClass : amountValueBaseClass));

const formatCurrency = (value?: number | null): string => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    return '0.00';
};

const formattedDiscountAmount = computed(() => formatCurrency(props.discount?.amount ?? null));

const clickedRemoveDiscount = (discount: Discount) => {
  processingRemove.value = true
  emit('emitRemoveDiscount', discount)
}

</script>

<style scoped>
.amount-value {
    position: relative;
    transition: color 0.2s ease;
}

.amount-value--loading {
    color: transparent;
    background-image: linear-gradient(
        110deg,
        #4b5563 0%,
        #9ca3af 35%,
        #f9fafb 50%,
        #9ca3af 65%,
        #4b5563 100%
    );
    background-size: 420% 100%;
    background-position: 380% 50%;
    animation: amount-value-shimmer 3s linear infinite;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

@keyframes amount-value-shimmer {
    0% {
        background-position: 380% 50%;
    }
    100% {
        background-position: -380% 50%;
    }
}
</style>